import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { AnimateOnScroll } from '../hooks/useScrollAnimation';
import axios from 'axios';

const BlogListPage = () => {
  const { t, language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const url = process.env.REACT_APP_BACKEND_URL || '';
        const res = await axios.get(`${url}/api/blog`);
        setPosts(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const getField = (post, field) => post[`${field}_${language}`] || post[`${field}_de`];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <Header />
      <main className="pt-32 pb-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-dark-grey dark:text-gray-400 hover:text-black dark:hover:text-white transition-colors mb-8">
            <ArrowLeft size={16} />
            {t('backToHome')}
          </Link>

          <h1 className="font-display text-4xl md:text-5xl font-bold text-black dark:text-white mb-4">
            {t('blog.pageTitle')}
          </h1>
          <p className="text-dark-grey dark:text-gray-400 text-lg mb-12">{t('blog.subtitle')}</p>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin w-8 h-8 border-2 border-mid-purple border-t-transparent rounded-full" />
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center text-dark-grey dark:text-gray-400 py-20">{t('blog.noPosts')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {posts.map((post, index) => (
                <AnimateOnScroll key={post.id} animation="fade-up" delay={0.1 * index}>
                  <Link to={`/blog/${post.id}`} className="group block h-full">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-grey/20 dark:border-gray-700 overflow-hidden card-hover h-full flex flex-col">
                      <div className="relative h-52 overflow-hidden">
                        <img
                          src={post.image || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&q=80'}
                          alt={getField(post, 'title')}
                          className="w-full h-full object-cover img-zoom"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-6 flex-1 flex flex-col">
                        <span className="text-xs text-mid-grey dark:text-gray-500 flex items-center gap-1 mb-3">
                          <Calendar size={12} />
                          {new Date(post.created_at).toLocaleDateString(language === 'de' ? 'de-DE' : language === 'ar' ? 'ar-SA' : 'en-US', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </span>
                        <h2 className="font-semibold text-xl text-black dark:text-white mb-2 group-hover:text-mid-purple transition-colors">
                          {getField(post, 'title')}
                        </h2>
                        <p className="text-dark-grey dark:text-gray-400 text-sm leading-relaxed flex-1">
                          {getField(post, 'excerpt')}
                        </p>
                        {post.tags && post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-4">
                            {post.tags.map((tag, i) => (
                              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-grey/10 dark:bg-gray-700 rounded-full text-xs text-dark-grey dark:text-gray-400">
                                <Tag size={10} />{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </AnimateOnScroll>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogListPage;
